import { Object as FabricObject } from 'fabric/fabric-impl';
import type { FabricEditor } from 'modules/fabric-editor';
import { saveBoard, selectActiveBoard, selectIsSaving } from 'modules/manager/store';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { useEditorDirty } from './use-editor-dirty';

export interface UseSaveBoardResult {
  save: () => Promise<void>;
  isDirty: boolean;
  isSaving: boolean;
  canSave: boolean;
}

export function useSaveBoard(editor: FabricEditor | null): UseSaveBoardResult {
  const dispatch = useAppDispatch();
  const board = useAppSelector(selectActiveBoard);
  const isSaving = useAppSelector(selectIsSaving);
  const isDirty = useEditorDirty(editor);

  const save = useCallback(async () => {
    if (!editor || !board) return;
    if (!editor.dirty?.isDirty()) return;
    if (isSaving) return;

    const json = editor.canvas.toJSON(['data']) as { version: string; objects: FabricObject[] };
    const result = await dispatch(saveBoard({ id: board.id, objects: json.objects ?? [], version: json.version }));
    if (saveBoard.fulfilled.match(result)) {
      editor.dirty?.markClean();
    }
  }, [editor, board, isSaving, dispatch]);

  return {
    save,
    isDirty,
    isSaving,
    canSave: isDirty && !isSaving && editor !== null && board !== null,
  };
}
